// The one time a service-account key is on screen, with what is needed to use
// it: the address an agent connects at, the header the key rides in, and a
// client configuration ready to paste (85.5).
import { useState } from 'react';
import { Link } from 'react-router';
import { buttonVariants } from '@/components/ui/Button';

export const KEY_HEADER = 'Authorization';
export const AGENT_GUIDE = '/docs/integration-api#endpoint-connect-an-ai-agent-mcp';

/** A client configuration in the `mcpServers` form MCP clients read. */
// eslint-disable-next-line react-refresh/only-export-components -- the block is tested on its own
export function mcpClientConfig(url: string, key: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        tres: {
          type: 'http',
          url,
          headers: { [KEY_HEADER]: `Bearer ${key}` },
        },
      },
    },
    null,
    2,
  );
}

function CopyButton({ text, label }: { text: string; label: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  return (
    <button
      type="button"
      aria-label={label}
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => setState('copied'),
          () => setState('failed'),
        );
      }}
      className={buttonVariants({ variant: 'secondary', size: 'sm' })}
    >
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Copy failed; select and copy' : 'Copy'}
    </button>
  );
}

export function ServiceAccountKeyPanel({
  name,
  secret,
  mcpPath,
  origin,
  onDismiss,
}: {
  name: string;
  secret: string;
  mcpPath: string | null;
  origin: string;
  onDismiss: () => void;
}) {
  const url = mcpPath ? `${origin}${mcpPath}` : null;
  const header = `${KEY_HEADER}: Bearer ${secret}`;
  return (
    <div className="space-y-3 rounded-md border border-tres-action/40 bg-tres-submenu-bg p-4">
      <div>
        <h3 className="mb-1 text-sm font-semibold text-tres-text-primary">Key for “{name}”, shown once</h3>
        <p className="text-xs text-tres-text-secondary">
          Store this key now. It cannot be shown again; if it is lost, revoke the account and create a new one.
        </p>
      </div>
      <div className="flex items-center gap-2">
        <CopyButton text={secret} label="Copy the key" />
        <code className="min-w-0 flex-1 overflow-x-auto rounded border border-border bg-card px-3 py-1.5 font-mono text-xs">
          {secret}
        </code>
      </div>

      {url ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold text-tres-text-primary">Connect an AI agent with this key</h4>
          <dl className="grid grid-cols-[max-content_1fr] items-center gap-x-3 gap-y-2 text-xs">
            <dt className="text-tres-text-secondary">Address</dt>
            <dd className="flex min-w-0 items-center gap-2">
              <CopyButton text={url} label="Copy the address" />
              <code className="min-w-0 overflow-x-auto font-mono">{url}</code>
            </dd>
            <dt className="text-tres-text-secondary">Header</dt>
            <dd className="flex min-w-0 items-center gap-2">
              <CopyButton text={header} label="Copy the header" />
              <code className="min-w-0 overflow-x-auto font-mono">{KEY_HEADER}: Bearer &lt;the key above&gt;</code>
            </dd>
          </dl>
          <p className="text-xs text-tres-text-secondary">
            Client configuration, for a client that reads MCP servers from a settings file:
          </p>
          <div className="flex items-start gap-2">
            <CopyButton text={mcpClientConfig(url, secret)} label="Copy the client configuration" />
            <pre className="min-w-0 flex-1 overflow-x-auto rounded border border-border bg-card px-3 py-2 font-mono text-xs">
              {mcpClientConfig(url, '<the key above>')}
            </pre>
          </div>
        </div>
      ) : (
        <p className="text-xs text-tres-text-secondary">
          This site&rsquo;s plan does not include connecting AI agents. The key works with the data API.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={onDismiss} className={buttonVariants({ variant: 'secondary', size: 'sm' })}>
          Dismiss
        </button>
        <Link to={AGENT_GUIDE} className="text-xs text-tres-action underline">
          How to connect tools and agents
        </Link>
      </div>
    </div>
  );
}
